/**
 * StreamerRewardsComponent
 * A specialized rewards component for streamers with tier-based restrictions
 * Streamers can only give rewards they own or have in inventory
 */
Vue.component('StreamerRewardsComponent', {
  template: templates.StreamerRewardsComponent,
  props: {
    rewards: {
      type: Object,
      required: false,
      default: () => null
    },
    tierAccess: {
      type: Object,
      required: true,
      default: () => ({})
    },
    inventory: {
      type: Object,
      required: true,
      default: () => ({ prime: 0, ultra: 0, legendary: 0, chest: 0 })
    },
    packs: {
      type: Object,
      required: true,
      default: () => ({})
    },
    chests: {
      type: Object,
      required: false,
      default: () => ({})
    },
    cards: {
      type: Object,
      required: false,
      default: () => ({})
    },
    ownedExclusiveCards: {
      type: Object,
      required: false,
      default: () => ({})
    },
    ownedShopItems: {
      type: Array,
      required: false,
      default: () => []
    },
    streamerPacks: {
      type: Array,
      required: false,
      default: () => []
    }
  },

  computed: {
    // Safe rewards object with fallback
    safeRewards() {
      return this.rewards || {
        xp: 0,
        barks: 0,
        givePack: [{ packId: null, packs_quantity: 0 }],
        exclusiveCard: [{ cardId: null, card_quantity: 0 }],
        giveChest: [{ chestId: null, chest_quantity: 0 }],
        giveShopItem: [{ itemId: null, itemType: null }],
        packs_amount: 0,
        exclusive_cards_amount: 0,
        chests_amount: 0,
        shop_items_amount: 0
      };
    },

    // Derive inventory from streamerPacks if available, fallback to inventory prop
    effectiveInventory() {
      if (this.streamerPacks && this.streamerPacks.length > 0) {
        const inventory = { prime: 0, ultra: 0, legendary: 0, chest: 0 };
        for (const sp of this.streamerPacks) {
          const type = sp.type.toLowerCase();
          inventory[type] = sp.count || 0;
        }
        return inventory;
      }
      return this.inventory;
    },

    // Filter packs based on tier and inventory (uses streamerPacks if available)
    // Only includes packs where: isChest=0, streamerShow=1, active=1
    availablePacks() {
      if (!this.tierAccess.allowedPacks) return {};

      // Helper to check if pack is valid for streamer selection
      const isValidPack = (pack) => {
        if (!pack) return false;
        // Must not be a chest
        if (pack.isChest === 1 || pack.isChest === true) return false;
        // Must have streamerShow enabled
        if (pack.streamerShow !== 1 && pack.streamerShow !== true) return false;
        // Must be active
        if (pack.active !== 1 && pack.active !== true) return false;
        return true;
      };

      // Use streamerPacks structure if available
      if (this.streamerPacks && this.streamerPacks.length > 0) {
        const filtered = {};
        const allowedTypes = this.tierAccess.allowedPacks || [];

        for (const sp of this.streamerPacks) {
          const packType = sp.type.toLowerCase();
          // Skip if type not allowed or no count
          if (!allowedTypes.includes(packType) || sp.count <= 0) continue;
          // Skip chests in pack filter (handled separately)
          if (packType === 'chest') continue;

          // Map each packId to the pack catalog
          for (const packId of (sp.packIds || [])) {
            const pack = this.packs[packId];
            if (isValidPack(pack)) {
              filtered[packId] = {
                ...pack,
                availableQuantity: sp.count,
                packType: packType
              };
            }
          }
        }
        return filtered;
      }

      // Fallback to existing inventory-based logic
      const filtered = {};
      for (const [key, pack] of Object.entries(this.packs)) {
        // Skip if pack doesn't meet criteria
        if (!isValidPack(pack)) continue;

        const packType = this.getPackType(pack.name);
        if (this.tierAccess.allowedPacks.includes(packType)) {
          // Check if there's inventory for this pack type
          if (this.effectiveInventory[packType] > 0) {
            filtered[key] = {
              ...pack,
              availableQuantity: this.effectiveInventory[packType]
            };
          }
        }
      }
      return filtered;
    },

    // Check if any packs are available
    hasAvailablePacks() {
      return Object.keys(this.availablePacks).length > 0;
    },

    // Check if chests are available
    canGiveChests() {
      if (!this.tierAccess.allowChests) return false;
      // Check if any chest has available quantity
      for (const chest of Object.values(this.chests)) {
        if (chest.availableQuantity > 0) return true;
      }
      return false;
    },

    // Check if shop items are available
    canGiveShopItems() {
      return this.tierAccess.allowOwnedShopItems && this.ownedShopItems.length > 0;
    },

    // Check if exclusive cards from reward shop are available
    hasOwnedExclusiveCards() {
      return Object.keys(this.ownedExclusiveCards).length > 0;
    },

    // Reward arrays
    rewardPacks() {
      return this.safeRewards.givePack || [];
    },

    rewardExclusiveCards() {
      return this.safeRewards.exclusiveCard || [];
    },

    rewardChests() {
      return this.safeRewards.giveChest || [];
    },

    rewardShopItems() {
      return this.safeRewards.giveShopItem || [];
    },

    // Counter properties
    packsAmount() {
      return this.safeRewards.packs_amount || 0;
    },

    exclusiveCardsAmount() {
      return this.safeRewards.exclusive_cards_amount || 0;
    },

    chestsAmount() {
      return this.safeRewards.chests_amount || 0;
    },

    shopItemsAmount() {
      return this.safeRewards.shop_items_amount || 0;
    },

    // Additional items (excluding first item)
    additionalPacks() {
      return this.rewardPacks.slice(1);
    },

    additionalExclusiveCards() {
      return this.rewardExclusiveCards.slice(1);
    },

    additionalChests() {
      return this.rewardChests.slice(1);
    },

    additionalShopItems() {
      return this.rewardShopItems.slice(1);
    },

    // Get total used packs by type for validation
    usedPacksByType() {
      const used = { prime: 0, ultra: 0, legendary: 0 };
      for (const pack of this.rewardPacks) {
        if (pack.packId) {
          const packData = this.packs[pack.packId];
          if (packData) {
            const packType = this.getPackType(packData.name);
            used[packType] += parseInt(pack.packs_quantity) || 0;
          }
        }
      }
      return used;
    },

    // Get remaining inventory after selections
    remainingInventory() {
      return {
        prime: this.effectiveInventory.prime - this.usedPacksByType.prime,
        ultra: this.effectiveInventory.ultra - this.usedPacksByType.ultra,
        legendary: this.effectiveInventory.legendary - this.usedPacksByType.legendary,
        chest: this.effectiveInventory.chest - this.totalChestsUsed
      };
    },

    totalChestsUsed() {
      let total = 0;
      for (const chest of this.rewardChests) {
        if (chest.chestId) {
          total += parseInt(chest.chest_quantity) || 0;
        }
      }
      return total;
    },

    // Get total used exclusive cards by cardId for validation
    usedExclusiveCardsByCardId() {
      const used = {};
      for (const card of this.rewardExclusiveCards) {
        if (card.cardId) {
          const quantity = parseInt(card.card_quantity) || 0;
          used[card.cardId] = (used[card.cardId] || 0) + quantity;
        }
      }
      return used;
    },

    // Get remaining quantity for each exclusive card
    remainingExclusiveCards() {
      const remaining = {};
      for (const [cardId, card] of Object.entries(this.ownedExclusiveCards)) {
        const owned = card.quantity || 1;
        const used = this.usedExclusiveCardsByCardId[cardId] || 0;
        remaining[cardId] = Math.max(0, owned - used);
      }
      return remaining;
    }
  },

  methods: {
    getPackType(packName) {
      if (!packName) return 'prime';
      const name = packName.toLowerCase();
      if (name.includes('legendary')) return 'legendary';
      if (name.includes('ultra')) return 'ultra';
      return 'prime';
    },

    // Emit reward change
    emitRewardChange(updatedRewards) {
      this.$emit('reward-changed', updatedRewards);
    },

    // Update simple fields
    updateField(field, value) {
      const parsedValue = parseInt(value) || 0;
      const updatedRewards = { ...this.safeRewards, [field]: parsedValue };
      this.emitRewardChange(updatedRewards);
    },

    // Generic array item getter
    getArrayItemValue(array, index, field, defaultValue = '') {
      if (array && array[index]?.hasOwnProperty(field)) {
        return array[index][field];
      }
      return typeof defaultValue === 'string' ? defaultValue : 0;
    },

    // Pack methods
    getPackValue(index, field) {
      const defaultValue = field === 'packId' ? '' : 0;
      return this.getArrayItemValue(this.rewardPacks, index, field, defaultValue);
    },

    incrementPack() {
      const currentArray = this.safeRewards.givePack || [];
      const newArray = [...currentArray, { packId: '', packs_quantity: 0 }];
      const currentCount = this.safeRewards.packs_amount || 0;

      const updatedRewards = {
        ...this.safeRewards,
        givePack: newArray,
        packs_amount: currentCount + 1
      };

      this.emitRewardChange(updatedRewards);
    },

    removePack(index) {
      const currentArray = this.safeRewards.givePack || [];
      const currentCount = this.safeRewards.packs_amount || 0;

      if (currentCount > 0 && index < currentArray.length) {
        const newArray = [...currentArray];
        newArray.splice(index, 1);

        const updatedRewards = {
          ...this.safeRewards,
          givePack: newArray,
          packs_amount: Math.max(0, currentCount - 1)
        };

        this.emitRewardChange(updatedRewards);
      }
    },

    updatePack(index, field, value) {
      const currentArray = this.safeRewards.givePack || [];
      const newArray = [...currentArray];

      while (newArray.length <= index) {
        newArray.push({ packId: '', packs_quantity: 0 });
      }

      const parsedValue = field === 'packId' ? value : (parseInt(value) || 0);
      newArray[index] = { ...newArray[index], [field]: parsedValue };

      const updatedRewards = {
        ...this.safeRewards,
        givePack: newArray
      };

      this.emitRewardChange(updatedRewards);
    },

    // Exclusive Card methods
    getExclusiveCardValue(index, field) {
      const defaultValue = field === 'cardId' ? '' : 0;
      return this.getArrayItemValue(this.rewardExclusiveCards, index, field, defaultValue);
    },

    incrementExclusiveCard() {
      const currentArray = this.safeRewards.exclusiveCard || [];
      const newArray = [...currentArray, { cardId: '', card_quantity: 0 }];
      const currentCount = this.safeRewards.exclusive_cards_amount || 0;

      const updatedRewards = {
        ...this.safeRewards,
        exclusiveCard: newArray,
        exclusive_cards_amount: currentCount + 1
      };

      this.emitRewardChange(updatedRewards);
    },

    removeExclusiveCard(index) {
      const currentArray = this.safeRewards.exclusiveCard || [];
      const currentCount = this.safeRewards.exclusive_cards_amount || 0;

      if (currentCount > 0 && index < currentArray.length) {
        const newArray = [...currentArray];
        newArray.splice(index, 1);

        const updatedRewards = {
          ...this.safeRewards,
          exclusiveCard: newArray,
          exclusive_cards_amount: Math.max(0, currentCount - 1)
        };

        this.emitRewardChange(updatedRewards);
      }
    },

    updateExclusiveCard(index, field, value) {
      const currentArray = this.safeRewards.exclusiveCard || [];
      const newArray = [...currentArray];

      while (newArray.length <= index) {
        newArray.push({ cardId: '', card_quantity: 0 });
      }

      const parsedValue = field === 'cardId' ? value : (parseInt(value) || 0);
      newArray[index] = { ...newArray[index], [field]: parsedValue };

      const updatedRewards = {
        ...this.safeRewards,
        exclusiveCard: newArray
      };

      this.emitRewardChange(updatedRewards);
    },

    // Get max quantity available for a given exclusive card
    getMaxExclusiveCardQuantity(cardId) {
      if (!cardId) return 0;
      const card = this.ownedExclusiveCards[cardId];
      if (!card) return 0;
      return this.remainingExclusiveCards[cardId] || 0;
    },

    // Get used quantity for a given exclusive card (for display)
    getUsedExclusiveCardQuantity(cardId) {
      return this.usedExclusiveCardsByCardId[cardId] || 0;
    },

    // Chest methods
    getChestValue(index, field) {
      const defaultValue = field === 'chestId' ? '' : 0;
      return this.getArrayItemValue(this.rewardChests, index, field, defaultValue);
    },

    incrementChest() {
      const currentArray = this.safeRewards.giveChest || [];
      const newArray = [...currentArray, { chestId: '', chest_quantity: 0 }];
      const currentCount = this.safeRewards.chests_amount || 0;

      const updatedRewards = {
        ...this.safeRewards,
        giveChest: newArray,
        chests_amount: currentCount + 1
      };

      this.emitRewardChange(updatedRewards);
    },

    removeChest(index) {
      const currentArray = this.safeRewards.giveChest || [];
      const currentCount = this.safeRewards.chests_amount || 0;

      if (currentCount > 0 && index < currentArray.length) {
        const newArray = [...currentArray];
        newArray.splice(index, 1);

        const updatedRewards = {
          ...this.safeRewards,
          giveChest: newArray,
          chests_amount: Math.max(0, currentCount - 1)
        };

        this.emitRewardChange(updatedRewards);
      }
    },

    updateChest(index, field, value) {
      const currentArray = this.safeRewards.giveChest || [];
      const newArray = [...currentArray];

      while (newArray.length <= index) {
        newArray.push({ chestId: '', chest_quantity: 0 });
      }

      const parsedValue = field === 'chestId' ? value : (parseInt(value) || 0);
      newArray[index] = { ...newArray[index], [field]: parsedValue };

      const updatedRewards = {
        ...this.safeRewards,
        giveChest: newArray
      };

      this.emitRewardChange(updatedRewards);
    },

    // Shop Item methods
    getShopItemValue(index, field) {
      return this.getArrayItemValue(this.rewardShopItems, index, field, '');
    },

    incrementShopItem() {
      const currentArray = this.safeRewards.giveShopItem || [];
      const newArray = [...currentArray, { itemId: '', itemType: '' }];
      const currentCount = this.safeRewards.shop_items_amount || 0;

      const updatedRewards = {
        ...this.safeRewards,
        giveShopItem: newArray,
        shop_items_amount: currentCount + 1
      };

      this.emitRewardChange(updatedRewards);
    },

    removeShopItem(index) {
      const currentArray = this.safeRewards.giveShopItem || [];
      const currentCount = this.safeRewards.shop_items_amount || 0;

      if (currentCount > 0 && index < currentArray.length) {
        const newArray = [...currentArray];
        newArray.splice(index, 1);

        const updatedRewards = {
          ...this.safeRewards,
          giveShopItem: newArray,
          shop_items_amount: Math.max(0, currentCount - 1)
        };

        this.emitRewardChange(updatedRewards);
      }
    },

    updateShopItemSelection(index, itemId) {
      const item = this.ownedShopItems.find(i => i.id === itemId);
      const itemType = item ? item.type : '';

      const currentArray = this.safeRewards.giveShopItem || [];
      const newArray = [...currentArray];

      while (newArray.length <= index) {
        newArray.push({ itemId: '', itemType: '' });
      }

      newArray[index] = { itemId: itemId, itemType: itemType };

      const updatedRewards = {
        ...this.safeRewards,
        giveShopItem: newArray
      };

      this.emitRewardChange(updatedRewards);
    },

    // Get max quantity for a pack based on inventory
    getMaxPackQuantity(packId) {
      if (!packId) return 0;
      const packData = this.packs[packId];
      if (!packData) return 0;
      const packType = this.getPackType(packData.name);
      return this.remainingInventory[packType] || 0;
    },

    // Get max chest quantity for a specific chest
    getMaxChestQuantity(chestId) {
      if (!chestId) return 0;
      const chest = this.chests[chestId];
      if (!chest) return 0;
      return chest.availableQuantity || 0;
    }
  }
});
